import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { getProjectSprintTaskDetail } from '@services/projects.service';
import { getSprintTaskApiId, getTaskDescriptionMarkdown } from '@hooks/projects/projectHelpers';
import { extractTaskDetailPayload, PROJECT_SPRINT_TASK_DETAIL_QUERY_KEY } from '@hooks/projects/useProjectSprints';

/**
 * Open {@link TaskDetailModal} for a sprint task from a lightweight link row (e.g. issue/upgrade `tasks[]`).
 */
export function useLinkedSprintTaskDetail(projectId) {
    const queryClient = useQueryClient();
    const [sprintTaskDetail, setSprintTaskDetail] = useState(null);
    const [sprintTaskDetailBugs, setSprintTaskDetailBugs] = useState([]);

    const closeLinkedSprintTaskDetail = useCallback(() => {
        setSprintTaskDetail(null);
        setSprintTaskDetailBugs([]);
    }, []);

    const openLinkedSprintTaskDetail = useCallback(
        async (linkedRow) => {
            const sprintId = linkedRow?.sprintId ?? linkedRow?.sprint?.id;
            if (!projectId || !sprintId) {
                message.warning('Missing sprint for this task.');
                return;
            }
            const sprint = {
                id: String(sprintId),
                name: linkedRow?.sprintName ?? linkedRow?.sprint?.name ?? '',
            };
            const taskId = getSprintTaskApiId(linkedRow) ?? linkedRow?.taskId ?? linkedRow?.id;
            if (!taskId) {
                message.warning('Missing task id.');
                return;
            }
            const taskShell = {
                ...linkedRow,
                id: linkedRow?.sprintTaskId ?? linkedRow?.id ?? linkedRow?.taskId,
                taskId: linkedRow?.taskId ?? taskId,
                status: linkedRow?.sprintTaskStatus ?? linkedRow?.status,
                title: linkedRow?.title ?? linkedRow?.name,
                name: linkedRow?.name ?? linkedRow?.title,
            };
            setSprintTaskDetail({ sprint, task: taskShell, mode: 'view' });
            setSprintTaskDetailBugs(Array.isArray(linkedRow?.bugs) ? linkedRow.bugs : []);
            try {
                const detail = await queryClient.fetchQuery({
                    queryKey: PROJECT_SPRINT_TASK_DETAIL_QUERY_KEY(projectId, sprint.id, taskId),
                    queryFn: () => getProjectSprintTaskDetail(projectId, sprint.id, taskId),
                    staleTime: 60_000,
                });
                const { task: fullTask, bugs } = extractTaskDetailPayload(detail, taskShell);
                setSprintTaskDetail({ sprint, task: fullTask, mode: 'view' });
                setSprintTaskDetailBugs(Array.isArray(bugs) ? bugs : []);
            } catch (err) {
                message.error(err?.response?.data?.message || err?.message || 'Failed to load task details');
            }
        },
        [projectId, queryClient],
    );

    const refreshLinkedSprintTaskDetail = useCallback(async () => {
        const t = sprintTaskDetail?.task;
        const s = sprintTaskDetail?.sprint;
        if (!t || !s?.id) return;
        await openLinkedSprintTaskDetail({
            ...t,
            sprintId: s.id,
            sprintName: s.name,
        });
    }, [openLinkedSprintTaskDetail, sprintTaskDetail?.task, sprintTaskDetail?.sprint]);

    const linkedSprintTaskDetailMarkdown = useMemo(() => {
        const t = sprintTaskDetail?.task;
        if (!t) return '';
        return getTaskDescriptionMarkdown(t);
    }, [sprintTaskDetail?.task]);

    return {
        linkedSprintTaskDetail: sprintTaskDetail,
        linkedSprintTaskDetailBugs: sprintTaskDetailBugs,
        linkedSprintTaskDetailMarkdown,
        openLinkedSprintTaskDetail,
        closeLinkedSprintTaskDetail,
        refreshLinkedSprintTaskDetail,
    };
}
